import {
    Accordion as ExpansionPanel,
    AccordionDetails as ExpansionPanelDetails,
    AccordionSummary as ExpansionPanelSummary,
    Box,
    Button,
    Divider,
    Typography,
} from '@mui/material';
import { Option } from 'components/ui/FormControls';
import React from 'react';

import {
    ArticleChangeHookType,
    ArticleState,
    ResearchRole,
} from '../../../shared/global-enums';
import {
    ArticleInfo,
    CategoryItemNode,
    Channel,
    User,
} from '../../../shared/types';
import ArticleDetails from './ArticleDetails';
import { useUser } from '../../providers/UserProvider';
import ArticleTaxonomies from './ArticleTaxonomies';
import { ArticleValidationError } from '../../../utils/validator.utils';
import { formatPublishDate } from '../../../utils/date.utils';

interface ButtonLabels {
    save: string;
    publish: string;
}

export interface ArticleSideBarProps {
    isNew: boolean;
    selectedContributors: string[];
    detailSummary: string;
    publishDate?: Date;
    selectedCategories: string[];
    selectedTags: string[];
    openToAll: boolean;
    expiryOffset: string;
    selectedChannel?: Channel;
    showWatermark: boolean;
    displayInChartSection: boolean;
    isPinned: boolean;
    isTimeless: boolean;
    isUserGuideArticle: boolean;
    userGuideScopes: string[];
    availableUserGuideScopes: Option[];
    userHasTableuAccess: boolean;

    listUsers: User[];
    preparedChannelsList: Channel[];
    treeCategories: CategoryItemNode[];
    listTags: Option[];

    formBusy: boolean;

    articleInfo: ArticleInfo;

    errorList: ArticleValidationError[];

    deleteDraft: () => void;
    archiveArticle: () => void;
    publishArticle: () => void;
    onChangeValue: (
        changeType: ArticleChangeHookType,
        value: string | string[] | boolean
    ) => void;
    onChangeMultiValue: (
        changeType: ArticleChangeHookType,
        value: Option[]
    ) => void;
}

export const ArticleSideBar = (props: ArticleSideBarProps) => {
    const { hasScope } = useUser();

    const createDetails = () => (
        <ArticleDetails
            selectedContributors={props.selectedContributors}
            detailSummary={props.detailSummary}
            publishDate={props.publishDate}
            listUsers={props.listUsers}
            canPublish={canPublish}
            errorList={props.errorList}
            openToAll={props.openToAll}
            showWatermark={props.showWatermark}
            displayInChartSection={props.displayInChartSection}
            isPinned={props.isPinned}
            isTimeless={props.isTimeless}
            isUserGuideArticle={props.isUserGuideArticle}
            userGuideScopes={props.userGuideScopes}
            availableUserGuideScopes={props.availableUserGuideScopes}
            userHasTableuAccess={props.userHasTableuAccess}
            selectedChannel={props.selectedChannel}
            preparedChannelsList={props.preparedChannelsList}
            selectedOffset={props.expiryOffset}
            onChangeValue={props.onChangeValue}
        />
    );

    const createTaxonomies = () => (
        <ArticleTaxonomies
            selectedCategories={props.selectedCategories}
            selectedTags={props.selectedTags}

            treeCategories={props.treeCategories}
            listTags={props.listTags}

            errorList={props.errorList}

            onChangeValue={props.onChangeValue}
            onChangeMultiValue={props.onChangeMultiValue}
        />
    );

    const canPublish =
        hasScope(ResearchRole.Editor) &&
        (props.isNew ||
            props.articleInfo.articleState === ArticleState.Pending ||
            props.articleInfo.articleState === ArticleState.Published);

    const articleStatus =
        props.articleInfo.articleState ?? ArticleState.Pending;
    const articleCreateDate = formatPublishDate(
        props.articleInfo.createDate,
        true
    );
    const articleEditDate = formatPublishDate(
        props.articleInfo.updateDate,
        true
    );
    const isArchived = props.articleInfo.articleState === ArticleState.Archived;

    const labels = getLabels(props.isNew, articleStatus);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        p: 2,
                    }}
                >
                    {canPublish && (
                        <Button
                            sx={{ flexBasis: '50%' }}
                            disabled={props.formBusy}
                            id="article-publish-button"
                            variant="contained"
                            size="medium"
                            onClick={props.publishArticle}
                            type="button"
                        >
                            {labels.publish}
                        </Button>
                    )}
                    <Button
                        sx={
                            canPublish
                                ? { ml: 1, flexBasis: '50%' }
                                : { width: '100%', height: 40 }
                        }
                        disabled={props.formBusy}
                        id="article-submit-button"
                        variant={canPublish ? 'outlined' : 'contained'}
                        color={canPublish ? 'secondary' : 'primary'}
                        size="medium"
                        type="submit"
                    >
                        {labels.save}
                    </Button>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box
                    sx={
                        props.isNew
                            ? { display: 'none' }
                            : { width: '100%', p: 2 }
                    }
                >
                    <Box sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                        <Typography component="span" variant="caption">
                            Status:
                        </Typography>
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, fontWeight: 'fontWeightBold' }}
                        >
                            {articleStatus}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                        <Typography component="span" variant="caption">
                            Last edit:
                        </Typography>
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, fontWeight: 'fontWeightBold' }}
                        >
                            {articleEditDate}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography component="span" variant="caption">
                            Created:
                        </Typography>
                        <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, fontWeight: 'fontWeightBold' }}
                        >
                            {articleCreateDate}
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ width: '100%' }} />
                {!props.isNew && !isArchived ? (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                width: '100%',
                                p: 2,
                            }}
                        >
                            <Button
                                id="article-archive-button"
                                disabled={props.formBusy}
                                type="button"
                                onClick={props.archiveArticle}
                                variant="outlined"
                                color="inherit"
                                size="small"
                            >
                                Archive article
                            </Button>
                        </Box>
                        <Divider sx={{ width: '100%' }} />
                    </>
                ) : null}
            </Box>
            <Box sx={{ bottom: 72, flex: 1 }}>
                <ExpansionPanel
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'visible',
                    }}
                    defaultExpanded={true}
                >
                    <ExpansionPanelSummary>
                        Article details
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails sx={{ bgcolor: 'background.paper' }}>
                        {createDetails()}
                    </ExpansionPanelDetails>
                </ExpansionPanel>
                <ExpansionPanel
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'visible',
                    }}
                    defaultExpanded={true}
                >
                    <ExpansionPanelSummary>Taxonomies</ExpansionPanelSummary>
                    <ExpansionPanelDetails sx={{ bgcolor: 'background.paper' }}>
                        {createTaxonomies()}
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            </Box>
        </Box>
    );
};

function getLabels(isNew: boolean, state: ArticleState): ButtonLabels {
    if (isNew) {
        return {
            save: 'Save to draft',
            publish: 'Publish',
        };
    }

    if (state === ArticleState.Published) {
        return {
            save: 'Convert to draft',
            publish: 'Update',
        };
    }

    if (state === ArticleState.Archived) {
        return {
            save: 'Update and unarchive',
            publish: 'Publish',
        };
    }

    return {
        save: 'Update draft',
        publish: 'Publish',
    };
}

export default ArticleSideBar;
